import { useMemo } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import VariantSelector from '@modules/product/component/variant-selector/VariantSelector';
import VariantSelectorMobile from '@modules/product/component/variant-selector/VariantSelector.mobile';
import QuantityGroup from '@modules/product/component/variant-purchase/QuantityGroup';
import StockAndShipping from '@modules/product/component/variant-purchase/StockAndShipping';
import SamplePurchase from '@modules/product/component/sample-purchase/SamplePurchase';
import { useSampleStock } from '@modules/product/hook/use-sample-stock';
import VariantPricing from '@modules/product/component/variant-pricing/VariantPricing';
import useVariantPrice from '../../hook/use-variant-price';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import SpecialtyPricingBanner from '@modules/product/component/specialty-pricing-banner/SpecialtyPricingBanner';
import type Product from '@modules/product/type/product';
import { ItemShippingProvider } from '@modules/ecommerce/context/ItemShippingContext';
import TradeOnlyBanner from '../trade-only-banner/TradeOnlyBanner';
import { VariantShippingMethodsProvider } from '~/modules/ecommerce/context/VariantShippingMethodsContext';

interface VariantsProps {
    data: {
        product: Product;
        ecommerceEnabled: boolean;
    };
    whereToBuyEnabled?: boolean;
    onClickWhereToBuy?: () => void;
}

const Variants = ({
    data: { product, ecommerceEnabled },
    whereToBuyEnabled,
    onClickWhereToBuy,
}: VariantsProps) => {
    const { mobile } = useAppDisplayModeContext();
    const pricingType = product.Pricing.PricingType;
    const { hasPrice: hasVariantPrice } = useVariantPrice(pricingType);
    const { selectedVariant, quantity } = useVariantSelectionState();
    const { canPurchase: sampleCanPurchase } = useSampleStock(selectedVariant);

    const sizesInUse = useMemo(
        () =>
            product.Sizes.filter(
                (s) =>
                    product.Variants.some((v) => v.Size?.ID === s.ID) ||
                    (product.Variants.length === 0 &&
                        product.MasterVariant?.Size?.ID === s.ID)
            ),
        [product]
    );

    const hasVariantShippingOptions =
        (selectedVariant?.ShippingProfile?.ShippingMethods ?? []).length > 0 ||
        (product.Professional.PickupLocations.length ?? 0) > 0;

    const hasShippingMethods = selectedVariant?.SampleShippingProfile
        ?.NoShipping
        ? false
        : (selectedVariant?.SampleShippingProfile?.ShippingMethods ?? [])
              .length > 0;

    const pickupLocations = product.Professional.PickupLocations;

    const hasSampleShippingOptions =
        hasShippingMethods || pickupLocations.length > 0;

    return (
        <VariantShippingMethodsProvider
            selectedVariant={selectedVariant}
            quantity={quantity}
            estimatedSampleWeight={product.Professional.SampleEstimatedWeight}
        >
            <ItemShippingProvider>
                {ecommerceEnabled && hasVariantPrice && (
                    <div className="mb-4 [&:empty]:hidden">
                        <SpecialtyPricingBanner product={product} />
                        <TradeOnlyBanner selectedVariant={selectedVariant} />
                    </div>
                )}
                <div className="md:mb-10 [&:empty]:hidden">
                    {mobile ? (
                        <VariantSelectorMobile
                            data={{
                                variants: product.Variants,
                                sizes: sizesInUse,
                            }}
                        />
                    ) : (
                        <VariantSelector
                            data={{
                                variants: product.Variants,
                                sizes: sizesInUse,
                            }}
                        />
                    )}
                    {hasVariantPrice &&
                        !product.Professional.PricingDisabled &&
                        selectedVariant?.CanViewPrice && (
                            <div className="mb-4 md:mt-8">
                                <VariantPricing pricingType={pricingType} />
                            </div>
                        )}
                    {product.EcommerceReady && (
                        <>
                            {pricingType === 'ecommercePricing' &&
                            hasVariantShippingOptions ? (
                                <div className="grid grid-cols-1 items-center gap-x-4 px-4.5 md:grid-cols-2 md:border-b md:border-black md:px-0 md:pb-8">
                                    <StockAndShipping />
                                    <QuantityGroup
                                        whereToBuyEnabled={whereToBuyEnabled}
                                        onClickWhereToBuy={onClickWhereToBuy}
                                    />
                                </div>
                            ) : sampleCanPurchase &&
                              hasSampleShippingOptions ? (
                                <SamplePurchase
                                    productId={product.ID}
                                    whereToBuyEnabled={whereToBuyEnabled}
                                    onClickWhereToBuy={onClickWhereToBuy}
                                />
                            ) : null}
                        </>
                    )}
                </div>
            </ItemShippingProvider>
        </VariantShippingMethodsProvider>
    );
};

export default Variants;
